import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useLoading } from "./LoadingContext";
import {
  shouldRedirectToOnboarding,
  checkAgeRestriction,
} from "./onboardingCheck";
import { Capacitor } from "@capacitor/core";

function ProtectedRoute({ children }) {
  const { currentUserState, loading } = useAuth();
  const { setLoading } = useLoading();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [isUnder17, setIsUnder17] = useState(false);
  const [checkingAge, setCheckingAge] = useState(false);

  React.useEffect(() => {
    setLoading(loading);
  }, [loading, setLoading]);

  // Check age restriction when user state changes
  useEffect(() => {
    const checkAgeRestrictionStatus = async () => {
      if (!currentUserState || loading) {
        setIsUnder17(false);
        setCheckingAge(false);
        return;
      }

      setCheckingAge(true);
      try {
        const isAgeRestricted = await checkAgeRestriction(currentUserState);
        setIsUnder17(isAgeRestricted);
      } catch (error) {
        console.error("Error checking age restriction:", error);
        setIsUnder17(false);
      } finally {
        setCheckingAge(false);
      }
    };

    checkAgeRestrictionStatus();
  }, [currentUserState, loading]);

  // Check onboarding status when user state changes
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!currentUserState || loading) {
        setShouldRedirect(false);
        setCheckingOnboarding(false);
        return;
      }

      setCheckingOnboarding(true);
      try {
        const needsOnboarding = await shouldRedirectToOnboarding(
          currentUserState
        );
        setShouldRedirect(needsOnboarding);
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        setShouldRedirect(false);
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboarding();
  }, [currentUserState, loading]);

  if (loading || checkingOnboarding || checkingAge) {
    return null;
  }

  if (!currentUserState) {
    return <Navigate to="/login" />;
  }

  // Check age restriction first
  if (isUnder17) {
    return <Navigate to="/age-restriction" />;
  }

  if (shouldRedirect) {
    // Platform-specific redirect: Web users go to download, Native users go to onboarding
    const isWebPlatform = !Capacitor.isNativePlatform();
    if (isWebPlatform) {
      return <Navigate to="/download" />;
    } else {
      return <Navigate to="/onboarding-start" />;
    }
  }

  return children;
}

export default ProtectedRoute;
